/**
 * @module constants/constants
 * @description Core UFE constants for environment configuration, rendering, and document types.
 * Provides foundational constants used throughout the UFE application.
 * This module has no dependencies on other modules.
 */

/**
 * @constant {string} INDEX_INJECTION - Identifier for index injection
 */
const INDEX_INJECTION = 'index';

/**
 * @constant {string} RENDERTYPE - Query parameter prefix for render type
 */
const RENDERTYPE = 'renderType=';

/**
 * @constant {string} DOCUMENT_TYPE - HTML5 doctype declaration
 * @description Standard HTML5 document type declaration with newline
 */
const DOCUMENT_TYPE = '<!DOCTYPE html>\n';

/**
 * @constant {string} EMPTY_DOCUMENT - Minimal valid HTML document
 * @description Empty HTML document with doctype, used as fallback or template
 */
const EMPTY_DOCUMENT = `${DOCUMENT_TYPE}<html></html>\n`;

/**
 * @constant {string} TARGETER_QUERY_PARAMS - Placeholder for targeter query parameters
 * @description Used in URL construction for A/B testing and targeting
 */
const TARGETER_QUERY_PARAMS = '__TARGETER_QUERY_PARAM__';

/**
 * @constant {string} ACTION_WATCHER_STATE_NAME - Redux state name for action watcher
 */
const ACTION_WATCHER_STATE_NAME = 'watcher';

/**
 * @constant {string} UFE_ENV_PRODUCTION - Production environment identifier
 * @description Used for runtime environment detection
 */
const UFE_ENV_PRODUCTION = 'PROD';

/**
 * @constant {string} UFE_ENV_QA - QA environment identifier
 * @description Used for runtime environment detection
 */
const UFE_ENV_QA = 'QA';

/**
 * @constant {string} UFE_ENV_LOCAL - Local development environment identifier
 * @description Used for runtime environment detection
 */
const UFE_ENV_LOCAL = 'LOCAL';

/**
 * @constant {string[]} VALID_UFE_ENVS - Array of valid UFE environment identifiers
 * @description Contains all recognized UFE environment values
 */
const VALID_UFE_ENVS = [UFE_ENV_PRODUCTION, UFE_ENV_LOCAL, UFE_ENV_QA];

/**
 * @constant {string} NODE_ENV_PRODUCTION - Node.js production environment constant
 * @description Build-time constant for production builds
 */
const NODE_ENV_PRODUCTION = 'production';

/**
 * @constant {string} NODE_ENV_DEVELOPMENT - Node.js development environment constant
 * @description Build-time constant for development builds
 */
const NODE_ENV_DEVELOPMENT = 'development';

/* eslint-disable no-useless-escape */
/**
 * @constant {RegExp} TARGETER_NAMES - Regular expression to match targeter names globally
 * @description Matches "targeterName": "value" patterns, accepts /, -, A-Z, 0-9, and _
 */
const TARGETER_NAMES = /\"targeterName\"\:[ ]?\"([\/|\w|\-]*)\"/g,
    /**
     * @constant {RegExp} TARGETER_NAME_GROUPS - Regular expression with capture groups for targeter names
     * @description Matches and captures "targeterName": "value" with separate groups
     */
    TARGETER_NAME_GROUPS = /(\"targeterName\"\:)[ ]?\"([\/|\w|\-]*)\"/;
/* eslint-enable  no-useless-escape */

/**
 * @constant {string} CONTENT_ENCODING_HEADER_LC - Content encoding header in lowercase
 * @description Lowercase version for header comparison
 */
const CONTENT_ENCODING_HEADER_LC = 'content-encoding',
    /**
     * @constant {string} CONTENT_ENCODING_HEADER - Content encoding header in proper case
     * @description Standard HTTP header name
     */
    CONTENT_ENCODING_HEADER = 'Content-Encoding';

export {
    CONTENT_ENCODING_HEADER_LC,
    CONTENT_ENCODING_HEADER,
    INDEX_INJECTION,
    DOCUMENT_TYPE,
    TARGETER_QUERY_PARAMS,
    ACTION_WATCHER_STATE_NAME,
    UFE_ENV_PRODUCTION,
    UFE_ENV_QA,
    UFE_ENV_LOCAL,
    VALID_UFE_ENVS,
    RENDERTYPE,
    EMPTY_DOCUMENT,
    NODE_ENV_PRODUCTION,
    NODE_ENV_DEVELOPMENT,
    TARGETER_NAMES,
    TARGETER_NAME_GROUPS
};
